import type {
    ICampaignDeadlinesConfig,
    ICampaignPlatformConfig,
    IDeliverableTypeConfig,
    IDurationRangeConfig,
    ICreatorPayoutFeeConfig,
    IInfluencerTierConfig,
    IUgcBudgetConfig,
} from '../types/global/ugcCampaignConfig.types';

/** Fallback when global config has not loaded or fields are missing (matches API defaults). */
export const DEFAULT_INFLUENCER_TIERS: IInfluencerTierConfig[] = [
    {
        id: 'beginner',
        name: 'Beginner',
        min_followers: 0,
        max_followers: 999,
        reach: '0–999',
        min_payout_per_creator_usd: 5,
        platform_fee_per_platform_usd: 10,
        sort_order: 0,
        active: true,
    },
    {
        id: 'nano',
        name: 'Nano',
        min_followers: 1000,
        max_followers: 9999,
        reach: '1k–10k',
        min_payout_per_creator_usd: 10,
        platform_fee_per_platform_usd: 12,
        sort_order: 1,
        active: true,
    },
    {
        id: 'micro',
        name: 'Micro',
        min_followers: 10000,
        max_followers: 49999,
        reach: '10k–50k',
        min_payout_per_creator_usd: 15,
        platform_fee_per_platform_usd: 15,
        sort_order: 2,
        active: true,
    },
    {
        id: 'mid',
        name: 'Mid-tier',
        min_followers: 50000,
        max_followers: 199999,
        reach: '50k–200k',
        min_payout_per_creator_usd: 100,
        platform_fee_per_platform_usd: 15,
        sort_order: 3,
        active: true,
    },
    {
        id: 'macro',
        name: 'Macro',
        min_followers: 200000,
        max_followers: 999999,
        reach: '200k–1M',
        min_payout_per_creator_usd: 200,
        platform_fee_per_platform_usd: 20,
        sort_order: 4,
        active: true,
    },
    {
        id: 'top-tier',
        name: 'Top-tier',
        min_followers: 1000000,
        max_followers: null,
        reach: '1M+',
        min_payout_per_creator_usd: 1000,
        platform_fee_per_platform_usd: 25,
        sort_order: 5,
        active: true,
    },
];

export const DEFAULT_CAMPAIGN_PLATFORMS: ICampaignPlatformConfig[] = [
    { id: 'instagram', label: 'Instagram', enabled: true, oauth_required: true, coming_soon: false },
    { id: 'tiktok', label: 'TikTok', enabled: true, oauth_required: true, coming_soon: false },
    { id: 'youtube', label: 'YouTube', enabled: true, oauth_required: true, coming_soon: false },
    { id: 'facebook', label: 'Facebook', enabled: false, oauth_required: false, coming_soon: true },
    { id: 'x', label: 'X', enabled: false, oauth_required: false, coming_soon: true },
    { id: 'reddit', label: 'Reddit', enabled: false, oauth_required: false, coming_soon: true },
    { id: 'thspian', label: 'Thspian', enabled: false, oauth_required: false, coming_soon: true },
];

export const DEFAULT_DELIVERABLE_TYPES: IDeliverableTypeConfig[] = [
    { id: 'instagram_post', label: 'Instagram Post', platform: 'instagram', enabled: true, sort_order: 0 },
    { id: 'instagram_reel', label: 'Instagram Reel', platform: 'instagram', enabled: true, sort_order: 1 },
    { id: 'instagram_story', label: 'Instagram Story', platform: 'instagram', enabled: true, sort_order: 2 },
    { id: 'tiktok_video', label: 'TikTok Video', platform: 'tiktok', enabled: true, sort_order: 3 },
    { id: 'youtube_short', label: 'YouTube Short', platform: 'youtube', enabled: true, sort_order: 4 },
    { id: 'youtube_video', label: 'YouTube Video', platform: 'youtube', enabled: true, sort_order: 5 },
    { id: 'twitter_post', label: 'Twitter / X Post', platform: 'twitter', enabled: true, sort_order: 6 },
    { id: 'facebook_post', label: 'Facebook Post', platform: 'facebook', enabled: false, sort_order: 7 },
    { id: 'ugc_video', label: 'UGC Video', enabled: true, sort_order: 8 },
    { id: 'ugc_photo', label: 'UGC Photo', enabled: true, sort_order: 9 },
];

export const DEFAULT_DURATION_RANGES: IDurationRangeConfig[] = [
    { id: 'n/a', label: 'Not applicable', enabled: true, sort_order: 0 },
    { id: '0-15s', label: 'Up to 15 seconds', enabled: true, sort_order: 1 },
    { id: '15-30s', label: '15 – 30 seconds', enabled: true, sort_order: 2 },
    { id: '30s-1min', label: '30 seconds – 1 minute', enabled: true, sort_order: 3 },
    { id: '1min-3min', label: '1 – 3 minutes', enabled: true, sort_order: 4 },
    { id: '3min-5min', label: '3 – 5 minutes', enabled: true, sort_order: 5 },
    { id: '5min+', label: '5 minutes+', enabled: true, sort_order: 6 },
];

export const DEFAULT_CREATOR_PAYOUT_FEE: ICreatorPayoutFeeConfig = {
    platform_fee_rate: 0.3,
    platform_fee_rate_subscriber: 0,
    waive_fee_when_host_referred: 'inactive',
};

export const DEFAULT_UGC_BUDGET: IUgcBudgetConfig = {
    absolute_min_total_budget_usd: 100,
    default_min_participants: 1,
    suggested_budget_multiplier_comfort: 1.25,
    suggested_budget_multiplier_strong: 1.5,
};

export const DEFAULT_CAMPAIGN_DEADLINES: ICampaignDeadlinesConfig = {
    auto_approve_days: 7,
    mark_complete_deadline_days: 7,
    pre_end_reminder_days: [3, 1],
};

/** Flat platform fee (USD cents) when a brand adds creator seats. Default $20. */
export const DEFAULT_SEAT_INCREASE_FEE_CENTS = 2000;
